import { NullFunction } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component } from '../../../concepts/objects';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { resolve } from '../../Container';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { InteractionHelper } from '../InteractionHelper';
import { Interaction, InteractionType, Physical } from '../entities/Physical';
import { Pose } from '../visualization/Pose';
import { ToolBehavior } from './ToolBehavior';

export class InteractionToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(InteractionHelper) private interaction!: InteractionHelper;

    private boundDispatch: any = NullFunction;
    private hintTarget?: Physical;

    public init() {
        this.burden(() => this.updateHintTarget(null, null, null));
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (mi.button !== MouseButtons.Main) return;
        if (state !== 'idle') return;
        if (mi.targetOverlay) return;

        // Double-click = Mutate
        // Single-click = Interact/Select

        const selectMode = mi.modifiers.shift || mi.modifiers.ctrl;
        const forceMode = mi.modifiers.alt;

        if (this.maybeInvoke(forceMode, selectMode)) {
            return 'invoking';
        }

        if (this.maybeDrag(forceMode, selectMode)) {
            return 'dragging';
        }

        if (mi.count === 2 && this.maybeMutate(forceMode, selectMode)) {
            return 'mutating';
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        if (state === 'idle') {

            const selectMode = mi.modifiers.shift || mi.modifiers.ctrl;
            const forceMode = mi.modifiers.alt;
            const types = ((!forceMode && !selectMode) ? ['invoke', 'drag', 'select'] : ['select']) as InteractionType[];

            const [hit, type, hint] = types.reduce((r, t) => {
                if (!!r.length) return r;
                // eslint-disable-next-line no-shadow
                const [hit, hint] = this.interaction.testFor(t, forceMode);
                if (hit) return [hit, t, hint];
                return [];
            }, []);

            this.updateHintTarget(hit, type, hint);

        }

        if (state === 'dragging') {
            this.boundDispatch?.(true);
            this.boundDispatch = null;
            return 'idle';
        }
    }

    public onMouseUp(state: string, _mi: MouseInput): string | void {
        if (state === 'dragging' || state === 'invoking' || state === 'mutating') {
            this.boundDispatch?.(false);
            this.boundDispatch = null;
            return 'idle';
        }
    }

    private updateHintTarget(next: Physical, nextType: string, nextHint: string) {

        if (!!this.hintTarget && this.hintTarget !== next) {
            if (this.hintTarget.alive) {
                Pose.update(this.hintTarget, { interaction: null });
            }
            this.hintTarget = null;
        }

        if (!this.hintTarget && !!next) {
            this.hintTarget = next;
        }

        if (this.hintTarget && this.hintTarget.alive) {
            const { interaction: prev } = Pose.get(this.hintTarget);
            if (prev?.type !== nextType || prev?.hint !== nextHint) {
                if (nextHint) {
                    Pose.update(this.hintTarget, {
                        interaction: Interaction(nextType as any, nextHint ?? null)
                    });
                }
                else {
                    Pose.update(this.hintTarget, {
                        interaction: null
                    });
                }
            }
        }
    }

    private maybeInvoke(forceMode: boolean, selectMode: boolean) {
        // No invoke if forcing or selecting
        if (forceMode) return false;
        if (selectMode) return false;
        // Test for the invoke
        const [hit, hint] = this.interaction.testFor('invoke', false);
        if (!hit) return false;
        // If passed, check there is an invocation command
        const { invocation } = Component.describe(hit);
        if (!invocation) return false;
        // If there is, bind for dispatch and return true
        this.boundDispatch = (...args: any[]) => hit?.alive && this.dispatch(invocation, [hit.id, hint, ...args]);
        return true;
    }

    private maybeDrag(forceMode: boolean, selectMode: boolean) {
        // No drag if forcing or selecting
        if (forceMode) return false;
        if (selectMode) return false;
        // Test for the invoke
        const [hit, hint] = this.interaction.testFor('drag', false);
        if (!hit) return false;
        // If passed, check there is an invocation command
        const { drag } = Component.describe(hit) as any;
        if (!drag) return false;
        // If there is, bind for dispatch and return true
        this.boundDispatch = (...args: any[]) => hit?.alive && this.dispatch(drag, [hit.id, hint, ...args]);
        this.updateHintTarget(hit, 'drag', hint);
        return true;
    }

    private maybeMutate(forceMode: boolean, selectMode: boolean) {
        // No ,utate if selecting
        if (selectMode) return false;
        // Test for the mutate
        const [hit, hint] = this.interaction.testFor('mutate', forceMode);
        if (!hit) return false;
        // If passed, check there is an mutation command
        const { mutation } = Component.describe(hit);
        if (!mutation) return false;
        // If there is, bind for dispatch and return true
        this.boundDispatch = () => hit?.alive && this.dispatch(mutation, [hit.id, hint]);
        return true;
    }
}
